import React, {useEffect, useMemo, useState}					from 'react';
import {useNavigate, useParams}						from 'react-router-dom';

import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import Select										from '../ressources/components/Select';
import {SelectValues}								from '../ressources/components/Select/interface';
import Textarea										from '../ressources/components/Textarea';
import Icon											from '../ressources/components/Icon';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';
import UsersModalAssignDevice						from '../Users/UserAssignDevice';
import OrderSelectModal								from '../Order/OrderSelectModal';
import DocumentItem									from '../ressources/components/Document';
import {DeviceStatus}								from '../ressources/components/Device';
import SettingsAddress								from '../Settings/SettingsAddress';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {pushUpload, setUploadPercent}				from '../redux/documents/documents';
import {postSupportsApi}							from '../redux/support/support';
import {PostSupportData}							from '../redux/support/interface';

import {ft_genNotification, ft_pushNotification} 	from '../ressources/functions/notification';
import {ft_formatAddressInline, ft_formatPrice,
	ft_formatUserName}								from '../ressources/functions/format';
import {ft_selectDataString,
	ft_selectDataStringIcon}						from '../ressources/functions/selectData';
import {ft_getDeviceImage, ft_getDeviceStatus}		from '../ressources/functions/deviceFunction';
import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_checkRight}								from '../ressources/functions/userFunction';

import img_deviceIcon								from '../ressources/images/icon/devices/computer.svg';
import img_upload									from '../ressources/images/icon/upload.svg';
import img_truck									from '../ressources/images/icon/truck.svg';
import img_delete									from '../ressources/images/icon/delete.svg';
import img_support									from '../ressources/images/icon/support.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './SupportNew.css';

interface modalAddressData {
	addressId: string,
	index?: number,
	duration?: number,
	keyboard?: string,
	deviceId?: string
}


function SupportNew() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const lang = useAppSelector(state => state.settings.lang);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const address = useAppSelector(state => state.address.data);
	const orders = useAppSelector(state => state.order.data);
	const supports = useAppSelector(state => state.support.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const documentsUpload = useAppSelector(state => state.documents.uploadDocument);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [type, set_type] = useState<SelectValues>();
	const [device, set_device] = useState<SelectValues>();
	const [order, set_order] = useState<SelectValues>();
	const [devicePattern, set_devicePattern] = useState<SelectValues>();
	const [description, set_description] = useState('');
	const [addressId, set_addressId] = useState('');
	const [modalAddress, set_modalAddress] = useState<modalAddressData | null>(null);
	const [modal, set_modal] = useState('');

	const [documents, set_documents] = useState<File[]>([]);

	const [load, set_load] = useState(false);

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	useEffect(() => {
		if (params.supportType !== 'device' && params.supportType !== 'order')
			return;

		set_type(params.supportType);

		if (params.supportType === 'device' && devicesWithLines.find(find => find._id === params.id))
			set_device(params.id);
		if (params.supportType === 'order' && orders.find(find => find._id === params.id))
			set_order(params.id);
	}, [params.supportType, devicesWithLines, orders]);

	useEffect(() => {
		if (!ft_checkRight(user, 'company.read'))
			set_type('device');
	}, [user]);

	function sendSupport() {
		if (!type || (type === 'order' && !order) || (type === 'device' && !device))
			return;

		set_load(true);

		const apiData: PostSupportData = {
			type,
			addressId,
			message:       description,
			order:         type === 'order' ? order as string : undefined,
			device:        type === 'device' ? device as string : undefined,
			devicePattern: type === 'device' ? devicePattern as string : undefined
		};

		// --- Post Support
		dispatch(postSupportsApi({data: apiData, user}))
		.then(async (response) => {
			if (!response?.payload?._id)
				return set_load(false);

			let notificationData = ft_genNotification(TEXTE, 'addSupportSuccess', img_support);
			notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

			// --- Upload Documents
			documents.map((document, i) => {
				if (document.size > 2097152)
					return;

				const index = documentsUpload.length + i;
				const dispatchData = {index, dispatch, function: setUploadPercent};

				const uploadFileData = {
					percent:  0,
					filename: document.name,
					size:     document.size,
					id:       response.payload._id
				};

				dispatch(pushUpload(uploadFileData));

				API.uploadSupportDocument(response.payload._id, document, user, dispatchData)
				.then((responseUpload) => {
					if (!responseUpload || !responseUpload.status)
						return dispatch(setUploadPercent({index, percent: `error: ${responseUpload?.error || ''}`}));

					notificationData = ft_genNotification(TEXTE, 'fileDownloadDone', img_upload, `${TEXTE.support.ticket} n°${response.payload.number}`);
					notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
				});
			});

			set_load(false);
			navigate(`/support/${response.payload._id}`);
		});
	}

	function renderSelection() {
		const orderData = order ? orders.find(find => find._id === order) : undefined;
		const deviceData = device ? devicesWithLines.find(find => find._id === device) : undefined;
		const userData = users.find(find => find?._id === deviceData?.user);

		return (
			<div className={'supportNewSelectionContainer'}>
				<span>{`2. ${TEXTE.support[type === 'order' ? 'selectOrder' : 'selectDevice']}*`}</span>

				<div className={'supportNewSelectionLine'}>
					{type === 'order' ?
						<Icon iconBorder icon={img_truck} iconColor={ft_cssVar('primary-600')} />
						:
						<div style={{backgroundImage: `url('${ft_getDeviceImage(deviceData)}')`}} />
					}
					<div className={'supportNewSelectionName'}>
						{type === 'order' ? `${TEXTE.menu.order} n°${orderData?.number}` : (deviceData?.shopData?.name || deviceData?.name)}
					</div>

					<div>
						{type === 'order' ?
							<DevicesBadges order={orderData} style={{justifyContent: 'center'}} />
							:
							<DeviceStatus device={deviceData} />
						}
					</div>

					<div className={'text-xs'}>
						{type === 'order' ?
							<span className={'primary-600 weight-500'}>{ft_formatPrice(orderData?.price)}<span className={'weight-400'}>{`/${TEXTE.global.month}`}</span></span>
							:
							<span className={'gray-600 weight-400'}>{ft_formatUserName(userData)}</span>
						}
					</div>

					<Icon
						icon={img_delete}
						onClick={() => {
							set_device(undefined);
							set_order(undefined);
						}}
					/>
				</div>
			</div>
		);
	}

	function renderContainer() {
		const selectData = [
			{value: 'device', icon: img_deviceIcon, text: TEXTE.support.device},
			{value: 'order', icon: img_truck, text: TEXTE.support.order},
			{value: 'mdm', icon: img_deviceIcon, text: TEXTE.support.mdm},
			{value: 'other', icon: img_support, text: TEXTE.global.other}
		];

		const actualAddress = address.find(find => find._id === addressId);
		const part3Disabled = (!type || (type === 'order' && !order) || (type === 'device' && !actualAddress));

		return (
			<div className={'supportNewContainer'}>
				<div className={'supportNewSelectLine'}>
					<span>{`1. ${TEXTE.support.problemConcerns}*`}</span>
					<div>
						<Select
							value={type}
							onChange={set_type}
							placeholder={TEXTE.support.chooseSubject}
							readOnly={!ft_checkRight(user, 'company.read')}
							data={ft_selectDataStringIcon(selectData, '#000')}
						/>
					</div>
				</div>

				{(type !== 'other' && type !== 'mdm') &&
					<>
						{((type === 'order' && order) || (type === 'device' && device)) ?
							renderSelection()
							:
							<div className={`supportNewSelectLine ${!type && 'disabled'}`}>
								<span>{`2. ${TEXTE.support[type === 'order' ? 'selectOrder' : 'selectDevice']}*`}</span>
								<div>
									<Select
										data={[]}
										readOnly={!type}
										onClick={() => set_modal(type as string)}
									/>
								</div>
							</div>
						}
					</>
				}

				{type === 'device' &&
					<>
						<div className={`supportNewSelectLine ${(!device) && 'disabled'}`}>
							<span>{`2.1 ${TEXTE.support.devicePattern}*`}</span>
							<div>
								<Select
									readOnly={!device}
									value={devicePattern}
									onChange={set_devicePattern}
									data={ft_selectDataString(TEXTE.support.patternList)}
								/>
							</div>
						</div>
						<div className={`supportNewSelectLine ${((!device) || (type === 'device' && !devicePattern)) && 'disabled'}`}>
							<span>{`2.2 ${TEXTE.support.deviceAddress}*`}</span>
							<div>
								<Select
									value={actualAddress ? 1 : undefined}
									readOnly={(!device || !devicePattern)}
									data={[{value: 1, render: ft_formatAddressInline(actualAddress)}]}
									onClick={() => set_modalAddress({addressId: actualAddress?._id || ''})}
								/>
							</div>
						</div>
					</>
				}

				<div className={`supportNewInputContainer flex ${part3Disabled && 'disabled'}`}>
					<span>{`3. ${TEXTE.support.problemDescription}`}</span>
					<Textarea
						flex
						required
						value={description}
						onChange={set_description}
						placeholder={TEXTE.support.descriptionPlaceHolder}
						readOnly={!!part3Disabled}
					/>
				</div>

				<div className={`supportNewInputContainer ${part3Disabled && 'disabled'}`}>
					<span>{`4. ${TEXTE.support.addPicture}`}</span>
					<Input
						type={'file'}
						fileInputText={TEXTE.support.documentsRules}
						placeholder={TEXTE.support.descriptionPlaceHolder}
						accept={'gif,.jpg,.jpeg,.tiff,.png,.webp,.bmp,.pdf'}
						onChangeFile={files => (files?.[0] ? set_documents([...documents, files[0]]) : null)}
					/>
				</div>

				<div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
					{renderDocuments()}
				</div>
			</div>
		);
	}

	function renderDocuments() {
		const render: React.ReactElement[] = [];

		documents.forEach((file, i) => {
			render.push(
				<DocumentItem
					size={file.size}
					filename={file.name}
					percent={file.size > 2097152 ? TEXTE.global.fileToBig : ' '}
					onClickDelete={() => {
						if (!documents)
							return;
						const copy: File[] = [...documents];

						copy.splice(i, 1);
						set_documents(copy);
					}}
				/>
			);
		});

		return render;
	}

	return (
		<div className={'supportNewMain'}>
			<div className={'supportNewHeader'}>
				<div className={'supportNewHeaderIcon'} />
				<div className={'supportNewHeaderText'}>
					<span className={'text-xxl weight-600 gray-900'}>{TEXTE.support.new}</span>
					<span className={'text-sm weight-400 gray-500'}>{`N°${supports.length + 1}`}</span>
				</div>
			</div>

			{renderContainer()}

			<div className={'supportNewBottom'}>
				<Button
					fullWidth
					onClick={sendSupport}
					disabled={load || (type !== 'other' && type !== 'mdm' && !device && !order) || !description}
				>
					{TEXTE.support.sendTicket}
				</Button>
			</div>

			{modal === 'device' &&
				<UsersModalAssignDevice
					modal
					onlyOneSelection
					onlyValalaDevice
					onClose={() => set_modal('')}
					deviceList={devicesWithLines.filter(e => ft_getDeviceStatus(e, users) !== 'locationEnd')}
					onSave={(selection) => {
						set_device(selection[0]);
						set_modal('');
					}}
				/>
			}
			{modal === 'order' &&
				<OrderSelectModal
					onlyOneSelection
					onClose={() => set_modal('')}
					onSave={(selection) => {
						set_order(selection[0]);
						set_modal('');
					}}
				/>
			}

			{modalAddress &&
				<SettingsAddress
					modal
					initialValue={modalAddress.addressId}
					onClose={() => set_modalAddress(null)}
					onConfirm={(newVal) => {
						set_addressId(newVal);
						set_modalAddress(null);
					}}
				/>
			}
		</div>
	);
}

export default SupportNew;
